import { CompanyParametersQuery, convertItemToBaseCurrency, CurrencyCollectionQuery, CurrencyItem, DocumentCalculatorBoPart, DocumentCalculatorModelPart, documentRecalculation, isDocumentReadyForCalculation, propertyChangedFilter, VatRateForSaleBaseQuery, VatRateItem } from '@skylab-shared/business';
import { BusinessObjectPartContext } from '@skylab-shared/core';
import { CalculationMethod, OrderDeliveryModel, OrderItemModel, VatTableItemModel } from '@skylab-shared/dataModel';
import { UnboxCollection } from '@skylab-shared/types';
import { Calc, MidpointRounding, withFlag } from '@skylab-shared/utils';
import { CalculatedDocument, DocumentCalculator } from '@skylab/calculations/document';
import { DocumentItem, DocumentItemGrossCalculator } from '@skylab/calculations/document-item';
import { OrderDocumentGrossCalculator, OrderDocumentNetCalculator } from '../calculators/order-document-calculator';

export interface OrderDocumentCalculatorModelPart extends DocumentCalculatorModelPart<OrderItemModel> {
    delivery: OrderDeliveryModel;
}

const OrderCalculatorsMap = {
    [CalculationMethod.Net]: OrderDocumentNetCalculator,
    [CalculationMethod.Gross]: OrderDocumentGrossCalculator
};

export class OrderDocumentCalculatorBoPart extends DocumentCalculatorBoPart<OrderDocumentCalculatorModelPart, OrderItemModel> {
    private deliveryCostCalculator = new DocumentItemGrossCalculator();

    public override async initializeAsync(context: BusinessObjectPartContext<OrderDocumentCalculatorModelPart>): Promise<void> {
        await super.initializeAsync(context);
        await this.bo.localContext.preload(VatRateForSaleBaseQuery);
        this.registerDeliveryCostCalculations();
        this.registerDeliveryPropertiesTriggerRecalculations();
    }

    private registerDeliveryPropertiesTriggerRecalculations(): void {
        this.coreFeature.propertyChanged.listen<OrderDeliveryModel>(propertyChangedFilter(
            OrderDeliveryModel,
            ['grossCost', 'netCost', 'taxExternalId'],
            _ => {
                withFlag(() => {
                    if (!isDocumentReadyForCalculation(this.bo.model)) return;
                    this.recalculateDocumentModelAndRewrite(this.bo.model);
                }, documentRecalculation(this.bo.model));
            }
        ));
    }

    private registerDeliveryCostCalculations(): void {
        this.coreFeature.propertyChanged.listen<OrderDeliveryModel>(propertyChangedFilter(
            OrderDeliveryModel,
            ['taxExternalId', 'grossCost'],
            _ => {
                const result = this.recalculateDeliveryCost();
                this.bo.model.delivery.grossCost = result.grossValue;
                this.bo.model.delivery.netCost = result.netValue;
            }
        ));
    }

    private recalculateDeliveryCost(): Readonly<Required<DocumentItem>> {
        if (!this.bo.model.currencyId) return { grossValue: 0, netValue: 0, vatValue: 0 } as Readonly<Required<DocumentItem>>;

        const { rate } = this.getVatRateItem(this.bo.model.delivery.taxExternalId);
        const { precision } = this.getCurrencyItem(this.bo.model.currencyId);

        return this.deliveryCostCalculator.recalculate({
            grossValue: this.bo.model.delivery.grossCost, vatRate: rate, grossPrice: 0, netPrice: 0, netValue: 0, no: 0, quantity: 1, vatRateId: this.bo.model.delivery.taxExternalId as unknown as number, vatValue: 0
        }, {precision }, 'grossValue');
    }

    protected recalculateDocumentModel(documentModel: OrderDocumentCalculatorModelPart): Readonly<{ inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }> {
        const result = { inDocumentCurrency: null, inBaseCurrency: null };

        const { systemCurrency } = this.bo.localContext.getPreloaded(CompanyParametersQuery);
        const inForeignCurrency = (documentModel.currencyId as unknown as string) !== this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { symbol: systemCurrency }).externalId;
        const { precision } = this.getCurrencyItem(documentModel.currencyId);

        const calculationContext = { precision };
        const calculator = new OrderCalculatorsMap[documentModel.calculationMethod as CalculationMethod.Net | CalculationMethod.Gross]();

        const inBaseCurrencyItems = documentModel.items
            .map(item => convertItemToBaseCurrency(
                this.convertDocumentItemToItem(item, {
                    rate: this.getVatRateItem(item.vatRateId).rate,
                    exchangeRate: { rate: 1, units: 1 },
                    calculationMethod: documentModel.calculationMethod,
                    precision
                }),
                inForeignCurrency && (documentModel.exchangeRate !== null) ? {
                    rate: documentModel.exchangeRate.rate,
                    units: documentModel.exchangeRate.units
                } : { rate: 1, units: 1 },
                precision
            ));

        if (calculator instanceof DocumentCalculator) {
            const calculated = calculator.recalculate(inBaseCurrencyItems as any, calculationContext);
            result.inBaseCurrency = this.calculateBaseCurrencyWithDeliveryCost(calculated) as any;

        }

        return result as any;
    }

    private calculateBaseCurrencyWithDeliveryCost(calculated: CalculatedDocument): CalculatedDocument {
        const precision = this.getCurrencyItem(this.bo.model.currencyId).precision;
        const deliveryCost = this.recalculateDeliveryCost();
        if (this.bo.model.calculationMethod === CalculationMethod.Net) {
            calculated.netValue = Calc.round(calculated.grossValue + deliveryCost.netValue, precision, MidpointRounding.AwayFromZero);
            calculated.vatValue = Calc.round(calculated.vatValue + deliveryCost.vatValue, precision, MidpointRounding.AwayFromZero);
            calculated.grossValue = Calc.round(calculated.netValue + calculated.vatValue, precision, MidpointRounding.AwayFromZero);
        } else {
            calculated.grossValue = Calc.round(calculated.grossValue + deliveryCost.grossValue, precision, MidpointRounding.AwayFromZero);
            calculated.vatValue = Calc.round(calculated.vatValue + deliveryCost.vatValue, precision, MidpointRounding.AwayFromZero);
            calculated.netValue = Calc.round(calculated.grossValue - calculated.vatValue, precision, MidpointRounding.AwayFromZero);
        }
        return calculated;
    }

    protected getVatRateItem(externalId: string): VatRateItem {
        return this.bo.localContext.firstPreloaded(VatRateForSaleBaseQuery, { externalId: externalId });
    }

    protected override getCurrencyItem(id: number | string): CurrencyItem {
        return this.bo.localContext.firstPreloaded(CurrencyCollectionQuery, { externalId: id as string });
    };

    protected override rewriteVatTableItemToVatTableItemModel(item: UnboxCollection<CalculatedDocument['vatTableItems']>, model: VatTableItemModel): void {
        model.netValue = item.netValue;
        model.vatValue = item.vatValue;
        model.grossValue = item.grossValue;
        // model.vatRateId = item.vatRateId; //TODO: póki co vattableModel ma vatRateId, ale nie potrzebujemy tego teraz, wiec zakomentowane.
    }

    protected override rewriteToDocumentModel(documentModel: OrderDocumentCalculatorModelPart, document: { inDocumentCurrency: CalculatedDocument, inBaseCurrency: CalculatedDocument }): void {
        documentModel.vatTableItems.clear();

        document.inBaseCurrency.vatTableItems.map(item => {
            const model = new VatTableItemModel();
            documentModel.vatTableItems.push(model);
            this.rewriteVatTableItemToVatTableItemModel(item, model);
        });

        documentModel.netValue = document.inBaseCurrency.netValue;
        documentModel.grossValue = document.inBaseCurrency.grossValue;
    }
}
